import { createContext, useContext } from 'react'
import { observable, action } from 'mobx'
import { ElementRef, LocationType, ExtentType, ConfigType } from './types/useMapStoreType'
import Map from '@arcgis/core/Map'
import MapView from '@arcgis/core/views/MapView'
import BasemapToggle from '@arcgis/core/widgets/BasemapToggle'
import Graphic from '@arcgis/core/Graphic'
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer'
import FeatureLayer from '@arcgis/core/layers/FeatureLayer'
import MapImageLayer from '@arcgis/core/layers/MapImageLayer'
import IdentityManager from '@arcgis/core/identity/IdentityManager'
import ServerInfo from '@arcgis/core/identity/ServerInfo'
import Sketch from '@arcgis/core/widgets/Sketch'
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel'
import Compass from '@arcgis/core/widgets/Compass'
import Locate from '@arcgis/core/widgets/Locate'
import Point from '@arcgis/core/geometry/Point'
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol'
// import CompositeSymbol from '@arcgis/core/symbols/CompositeSymbol'
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol'
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters'
import * as identify from '@arcgis/core/rest/identify.js'

import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol.js'
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol.js'

class MapStore {
    _map = {} as __esri.Map
    _view = {} as __esri.MapView
    _layerMapOra = {} as __esri.MapImageLayer
    _layerRaster = {} as __esri.MapImageLayer
    _layerIdentify = {} as __esri.MapImageLayer
    _graphicsLayer = {} as __esri.GraphicsLayer
    _featureLayerPoint = {} as __esri.FeatureLayer
    _featureLayerPolygon = {} as __esri.FeatureLayer
    _sketchViewModel = {} as __esri.SketchViewModel
    _sketch = {} as __esri.Sketch
    _identifyParams = new IdentifyParameters({
        layerIds: [0],
        tolerance: 20,
    })
    _pictureMarkerSymbol = new PictureMarkerSymbol({
        url: `${process.env.REACT_APP_IMAGE_PATH}/assets/image/pin_station.png`,
        width: '35px',
        height: '35px',
    })

    // _cimSymbol = new CIMSymbol({
    //     data: {
    //         type: 'CIMSymbolReference',
    //         symbol: {
    //             type: 'CIMPointSymbol',
    //             symbolLayers: [
    //                 {
    //                     type: 'CIMPictureMarker',
    //                     enable: true,
    //                     anchorPoint: {
    //                         x: 0,
    //                         y: 0,
    //                     },
    //                     anchorPointUnits: 'Relative',
    //                     dominantSizeAxis3D: 'Y',
    //                     size: 50,
    //                     billboardMode3D: 'FaceNearPlane',
    //                     invertBackfaceTexture: true,
    //                     scaleX: 1,
    //                     textureFilter: 'Picture',
    //                     tintColor: [255, 255, 255, 255],
    //                     url: `https://www.iconpacks.net/icons/1/free-pin-icon-48-thumb.png`,
    //                 },
    //             ],
    //         },
    //     },
    // })

    _cimSymbol = new CIMSymbol({
        data: {
            type: 'CIMSymbolReference',
            symbol: {
                type: 'CIMPointSymbol',
                symbolLayers: [
                    {
                        type: 'CIMVectorMarker',
                        enable: true,
                        size: 32,
                        frame: {
                            xmin: 0,
                            ymin: 0,
                            xmax: 16,
                            ymax: 16,
                        },
                        markerGraphics: [
                            {
                                type: 'CIMMarkerGraphic',
                                geometry: {
                                    rings: [
                                        [
                                            [8, 16],
                                            [0, 0],
                                            [16, 0],
                                            [8, 16],
                                        ],
                                    ],
                                },
                                symbol: {
                                    type: 'CIMPolygonSymbol',
                                    symbolLayers: [
                                        {
                                            type: 'CIMSolidStroke',
                                            width: 5,
                                            color: [240, 94, 35, 255],
                                            enable: true,
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                ],
            },
        },
    })
    _simpleMarkerSymbol = new SimpleMarkerSymbol({
        color: [226, 119, 40],
        outline: {
            color: [0, 0, 0],
            width: 1,
        },
    })
    _poloygonMarkerSymbolRed = new SimpleFillSymbol({
        color: [0, 0, 0, 0], // [r, g, b, opacity]
        outline: {
            color: [255, 0, 0], // red
            width: 2,
        },
    })
    _poloygonMarkerSymbolGreen = new SimpleFillSymbol({
        color: [0, 0, 0, 0], // [r, g, b, opacity]
        outline: {
            color: [0, 255, 0], // green
            width: 2,
        },
    })
    _poloygonMarkerSymbolBlue = new SimpleFillSymbol({
        color: [0, 0, 0, 0], // [r, g, b, opacity]
        outline: {
            color: [0, 0, 255], // blue
            width: 2,
        },
    })
    _poloygonMarkerSymbolYellow = new SimpleFillSymbol({
        color: [0, 0, 0, 0], // [r, g, b, opacity]
        outline: {
            // color: [255, 255, 0], // yellow
            color: [255, 179, 25], // yellow
            width: 2,
        },
    })
    _poloygonMarkerSymbolBlack = new SimpleFillSymbol({
        color: [0, 0, 0, 0], // [r, g, b, opacity]
        outline: {
            color: [0, 0, 0], // black
            width: 2,
        },
    })

    @observable location: LocationType | null = null
    @action setLocation = (param: LocationType) => {
        this.location = param
    }

    @observable locationPointer: __esri.Point | null = null
    @action setLocationPointer = (param: __esri.Point) => {
        this.locationPointer = param
    }

    @observable locationExtent: ExtentType | null = null
    @action setLocationExtent = (param: ExtentType) => {
        this.locationExtent = param
    }

    createMap = (props: ElementRef) => {
        const configDTV = {
            url: `${process.env.REACT_APP_MAP_DTV_URL}`,
            layerUrl: [`${process.env.REACT_APP_MAP_DTV_LAYER_URL}`, `${process.env.REACT_APP_MAP_DTV_LAYER_URL}/0`],
            username: `${process.env.REACT_APP_MAP_DTV_USER}`,
            password: `${process.env.REACT_APP_MAP_DTV_PW}`,
        }
        const configMapOra = {
            url: `${process.env.REACT_APP_MAP_ARCGIS_SERVER_URL}`,
            layerUrl: [
                `${process.env.REACT_APP_MAP_DTT_IMAGE_LAYER_URL}`,
                `${process.env.REACT_APP_MAP_DTT_IMAGE_LAYER_URL}/0`,
                `${process.env.REACT_APP_MAP_DTT_IMAGE_LAYER_URL}/1`,
                `${process.env.REACT_APP_MAP_DTT_IMAGE_LAYER_URL}/2`,
                `${process.env.REACT_APP_MAP_DTT_IMAGE_LAYER_URL}/3`,
            ],
            username: `${process.env.REACT_APP_MAP_DTT_LAYER_USER}`,
            password: `${process.env.REACT_APP_MAP_DTT_LAYER_PW}`,
        }

        const loadDTVMap = this.registerTokenAsync(configDTV, 'B2')
        const loadMapOra = this.registerTokenAsync(configMapOra, 'C3')

        Promise.all([loadDTVMap]).then(() => {
            this._graphicsLayer = new GraphicsLayer()
            this._featureLayerPoint = new FeatureLayer({
                url: configDTV.layerUrl[1],
            })
            this._featureLayerPolygon = new FeatureLayer({
                url: configMapOra.layerUrl[2],
            })
            this._layerMapOra = new MapImageLayer({
                url: configMapOra.layerUrl[0],
                id: 'mapOraLayer',
            })
            // this._layerMapOra = new MapImageLayer({
            //     url: configMapOra.layerUrl[0],
            //     sublayers: [
            //         {
            //             id: 0,
            //             visible: false,
            //         },
            //         {
            //             id: 1,
            //             visible: false,
            //         },
            //         {
            //             id: 2,
            //             visible: true,
            //         },
            //         {
            //             id: 3,
            //             visible: false,
            //         },
            //     ],
            // })
            // this._layerIdentify = new MapImageLayer({
            //     url: configDTV.layerUrl[0],
            //     id: 'identifyLayer',
            // })

            this._map = new Map({
                basemap: 'streets-vector',
                layers: [this._layerMapOra],
                // layers: [this._layerMapOra, this._layerIdentify],
                // layers: [this._layerRaster, this._layerMapOra],
            })
            this._map.add(this._graphicsLayer)

            this._view = new MapView({
                map: this._map,
                container: props.elementDiv,
                center: [100, 13], // longitude, latitude
                zoom: 6,
            })

            // Add graphic layer
            this.queryFeatureAndAddGraphicPoint()
            // this.queryFeatureAndAddGraphicPolygon()

            // Remove all default widget
            this._view.ui.remove('attribution')
            // this._view.ui.remove('zoom')

            // Add ui components
            this.addPositionComponent(this._view, props)

            // Add event
            this.setupMapViewEventListener(this._view, 'pointer-move')
            this.setupMapViewEventListener(this._view, 'mouse-wheel')
            this.setupMapViewEventListener(this._view, 'drag')

            // Identify map
            this._view.on('click', (event) => {
                // console.log('adada');
                // const startTime = new Date().getTime()
                // this.queryMap(event, startTime)
                // this.identifyMap(event, configDTV.layerUrl[0], startTime)
            })
        })
    }

    queryMap = (event: any, startTime: number) => {
        let featureLayerPoint2 = this._featureLayerPoint
        this._view.hitTest(event).then((response: any) => {
            if (response.results.length) {
                let graphic = response.results.filter((result: any) => {
                    return result.graphic.layer
                })[0].graphic

                let query = featureLayerPoint2.createQuery()
                query.where = `OBJECTID = ${graphic.attributes.OBJECTID}`

                featureLayerPoint2.queryFeatures(query).then((response) => {
                    console.log('result of query map >>>')
                    response.features.forEach((item: any) => {
                        console.log(item.attributes)
                    })
                    const endTime = new Date().getTime()
                    this.calDurationTime(startTime, endTime, 'query map')
                })
            }
        })
    }

    identifyMap = (event: any, identifyLayer: string, startTime: number) => {
        this._identifyParams.geometry = event.mapPoint
        this._identifyParams.mapExtent = this._view.extent
        identify.identify(identifyLayer, this._identifyParams).then((response: any) => {
            console.log('result of identify map >>>')
            response.results.forEach((item: any) => {
                console.log(item.feature.attributes)
            })
            const endTime = new Date().getTime()
            this.calDurationTime(startTime, endTime, 'identify map')
        })
    }

    calDurationTime = (startTime: number, endTime: number, functionName?: string) => {
        const seconds = 1000 * 60 * 60
        const minutes = seconds * 60
        const hours = minutes * 60
        const days = hours * 24
        const years = days * 365

        const durationMilliseconds = endTime - startTime
        console.log(`duration of ${functionName} = ${Math.floor(durationMilliseconds)} ms`)
    }

    addPositionComponent = (view: __esri.MapView, props: ElementRef) => {
        let compassWidget = new Compass({
            view: view,
            id: 'myCompassWidget',
        })

        let basemapGallery = new BasemapToggle({
            view: this._view,
            nextBasemap: 'hybrid',
        })

        let locateWidget = new Locate({
            view: view,
            graphic: new Graphic({
                symbol: this._simpleMarkerSymbol,
            }),
        })

        view.ui.padding = { top: 16, left: 16, right: 16, bottom: 30 }

        view.ui.add([props.componentFooter], 'manual')
        props.componentFooter?.setAttribute('class', 'esri-component')
        props.componentFooter?.setAttribute(
            'style',
            'bottom: 0px; width: -moz-available; width: -webkit-fill-available;',
        )

        switch (view.widthBreakpoint) {
            case 'xsmall':
                view.ui.add([props.componentTopLeft, props.componentTopRight, compassWidget], 'top-right')
                view.ui.add([basemapGallery], 'bottom-left')
                view.ui.add([props.componentBottomCenter], 'manual')
                props.componentBottomCenter?.setAttribute('class', 'esri-component')
                props.componentBottomCenter?.setAttribute(
                    'style',
                    'bottom: 30px; left: 0px; right: 0px; box-shadow: none; margin-left: 95px; margin-right: 70px;',
                )
                view.ui.add([locateWidget], 'bottom-right')
                break
            case 'small':
                view.ui.add([props.componentTopLeft, props.componentTopRight, compassWidget], 'top-right')
                view.ui.add([basemapGallery], 'bottom-left')
                view.ui.add([props.componentBottomCenter], 'manual')
                props.componentBottomCenter?.setAttribute('class', 'esri-component')
                props.componentBottomCenter?.setAttribute(
                    'style',
                    'bottom: 30px; left: 0px; right: 0px; box-shadow: none; margin-left: 95px; margin-right: 70px;',
                )
                view.ui.add([locateWidget], 'bottom-right')
                break
            case 'medium':
            case 'large':
            case 'xlarge':
                view.ui.add([props.componentTopLeft], 'top-left')
                props.componentTopLeft?.setAttribute('class', 'esri-component')
                props.componentTopLeft?.setAttribute('style', 'width: 100%;')
                view.ui.add([props.componentTopRight, compassWidget], 'top-right')
                props.componentTopRight?.setAttribute('class', 'esri-component')
                props.componentTopRight?.setAttribute(
                    'style',
                    'box-shadow: none; margin-bottom: 0px; align-items: center;',
                )
                view.ui.add([basemapGallery], 'bottom-left')
                view.ui.add([props.componentBottomCenter], 'manual')
                props.componentBottomCenter?.setAttribute('class', 'esri-ui-manual-container esri-component')
                props.componentBottomCenter?.setAttribute(
                    'style',
                    'bottom: 50px; left: 0px; right: 0px; box-shadow: none; margin-left: 130px; margin-right: 100px;',
                )
                view.ui.add([locateWidget, props.componentBottomRight], 'bottom-right')
                break
            default:
        }

        view.watch('widthBreakpoint', function (breakpoint) {
            switch (breakpoint) {
                case 'xsmall':
                    view.ui.add([props.componentTopLeft, props.componentTopRight, compassWidget], 'top-right')
                    view.ui.add([basemapGallery], 'bottom-left')
                    view.ui.add([props.componentBottomCenter], 'manual')
                    props.componentBottomCenter?.setAttribute('class', 'esri-component')
                    props.componentBottomCenter?.setAttribute(
                        'style',
                        'bottom: 30px; left: 0px; right: 0px; box-shadow: none; margin-left: 95px; margin-right: 70px;',
                    )
                    view.ui.add([locateWidget], 'bottom-right')
                    break
                case 'small':
                    view.ui.add([props.componentTopLeft, props.componentTopRight, compassWidget], 'top-right')
                    view.ui.add([basemapGallery], 'bottom-left')
                    view.ui.add([props.componentBottomCenter], 'manual')
                    props.componentBottomCenter?.setAttribute('class', 'esri-component')
                    props.componentBottomCenter?.setAttribute(
                        'style',
                        'bottom: 30px; left: 0px; right: 0px; box-shadow: none; margin-left: 95px; margin-right: 70px;',
                    )
                    view.ui.add([locateWidget], 'bottom-right')
                    break
                case 'medium':
                case 'large':
                case 'xlarge':
                    view.ui.add([props.componentTopLeft], 'top-left')
                    props.componentTopLeft?.setAttribute('class', 'esri-component')
                    props.componentTopLeft?.setAttribute('style', 'width: 100%;')
                    view.ui.add([props.componentTopRight, compassWidget], 'top-right')
                    props.componentTopRight?.setAttribute('class', 'esri-component')
                    props.componentTopRight?.setAttribute('style', 'box-shadow: none; margin-bottom: 0px;')
                    view.ui.add([basemapGallery], 'bottom-left')
                    view.ui.add([props.componentBottomCenter], 'manual')
                    props.componentBottomCenter?.setAttribute('class', 'esri-ui-manual-container esri-component')
                    props.componentBottomCenter?.setAttribute(
                        'style',
                        'bottom: 50px; left: 0px; right: 0px; box-shadow: none; margin-left: 130px; margin-right: 100px;',
                    )
                    view.ui.add([locateWidget, props.componentBottomRight], 'bottom-right')
                    break
                default:
            }
        })
    }

    executeIdentify = (url: string, view: __esri.MapView, idenParam: any) => {
        view.on('click', function (evt) {
            idenParam.geometry = evt.mapPoint
            idenParam.mapExtent = view.extent
            idenParam.width = view.width
            idenParam.height = view.height

            identify.identify(url, idenParam).then(function (response) {
                let results = response.results
                if (results.length > 0) {
                    let content =
                        '<b>OBJECTID : </b> ' +
                        results[0].feature.attributes.OBJECTID +
                        '<br/>' +
                        '<b>POP2000 : </b>' +
                        results[0].feature.attributes.POP2000 +
                        '<br/>'

                    results[0].feature.popupTemplate = {
                        title: results[0].feature.attributes.AREANAME,
                        content: content,
                    }

                    if (view.popup) {
                        view.popup.open({
                            features: [results[0].feature],
                            location: evt.mapPoint,
                        })
                    }
                }
            })
        })
    }

    setupMapViewEventListener = (view: MapView, name: string) => {
        switch (name) {
            case 'pointer-move':
                // this.setLocationPointer(point)
                view.on(name, (value: any) => {
                    const point = view.toMap({ x: value.x, y: value.y })
                    let foo = document.getElementById('div-lat-lon')
                    if (foo && point.latitude && point.longitude)
                        foo.innerHTML = `${point.latitude.toFixed(5)}, ${point.longitude.toFixed(5)}`
                })
                break

            case 'mouse-wheel':
                // this.getExtentMapView()
                break

            case 'drag':
                // this.getExtentMapView()
                break

            default:
                break
        }
    }

    getExtentMapView = () => {
        const { xmax, xmin, ymax, ymin } = this._view.extent
        this.setLocationExtent({ xmax, xmin, ymax, ymin })
    }

    createPoint = (param: LocationType) => {
        const { latitude, longitude } = param

        let point: any = {
            type: 'point',
            longitude,
            latitude,
        }

        const pointGraphic: __esri.Graphic = new Graphic({
            geometry: point,
            symbol: this._simpleMarkerSymbol,
        })

        this._map.add(this._graphicsLayer)
        this._graphicsLayer.add(pointGraphic)
    }

    addSketch = () => {
        this._sketch = new Sketch({
            layer: this._graphicsLayer,
            view: this._view,
            creationMode: 'update',
        })

        this._sketch.on('create', (event) => {
            if (event.state === 'complete') {
                console.log('sketch create complete --- ', event)
            }
        })

        this._sketch.on('update', (event) => {
            if (event.state === 'start') {
                console.log('sketch update start --- ', event)
            }
        })

        this._sketch.visibleElements = {
            createTools: {
                point: true,
            },
            selectionTools: {
                'lasso-selection': false,
            },
            settingsMenu: false,
        }

        this._view.ui.add(this._sketch, {
            position: 'top-left',
        })
    }

    addSketchViewModel = (tool: string) => {
        this._sketchViewModel = new SketchViewModel({
            layer: this._graphicsLayer,
            view: this._view,
            pointSymbol: {
                type: 'simple-marker',
                style: 'circle',
                size: 12,
                color: [226, 119, 40],
                outline: {
                    color: [0, 0, 0],
                    width: 1,
                },
            },
        })

        switch (tool) {
            case 'point':
                this._sketchViewModel.create(tool, { mode: 'click' })
                break

            case 'polyline':
                this._sketchViewModel.create(tool, { mode: 'click' })
                break

            case 'polygon':
                this._sketchViewModel.create(tool, { mode: 'click' })
                break

            case 'circle':
                this._sketchViewModel.create(tool, { mode: 'freehand' })
                break

            default:
                break
        }

        this._sketchViewModel.on('create', (event) => {
            if (event.state === 'complete') {
                const geometry = event.graphic.geometry as any
                this.setLocation({
                    latitude: geometry.latitude,
                    longitude: geometry.longitude,
                })
            }
        })
    }

    registerTokenAsync = (config: ConfigType, retValue = 'A') => {
        const { url, username, password } = config

        const promiseRegisToken = new Promise((resolve, reject) => {
            const serverInfo = new ServerInfo({
                tokenServiceUrl: url + '/tokens/generateToken',
                hasServer: true,
            })
            IdentityManager.generateToken(serverInfo, {
                username: username,
                password: password,
            }).then((res) => {
                IdentityManager.registerToken({
                    server: url + '/rest/services',
                    token: res.token,
                    expires: res.expires,
                })
                resolve(retValue)
            })
        })
        return promiseRegisToken
    }

    queryFeatureAndAddGraphicPoint = () => {
        let query = this._featureLayerPoint.createQuery()
        query.where = '1=1'
        query.outFields = ['*']

        this._featureLayerPoint.queryFeatures(query).then((response) => {
            response.features.forEach((item) => {
                const geometry = item.geometry as Point
                if (geometry.longitude && geometry.latitude) {
                    const point = new Point({
                        longitude: geometry.longitude,
                        latitude: geometry.latitude,
                    })
                    const graphic = new Graphic({
                        geometry: point,
                        // geometry: item.geometry,
                        // symbol: this._cimSymbol,
                        symbol: this._pictureMarkerSymbol,
                        attributes: item.attributes,
                    })
                    this._graphicsLayer.add(graphic)
                }
            })
        })
    }

    queryFeatureAndAddGraphicPolygon = () => {
        let query = this._featureLayerPolygon.createQuery()
        query.where = '1=1'
        query.outFields = ['*']

        this._featureLayerPolygon.queryFeatures(query).then((response) => {
            response.features.forEach((item) => {
                const graphic = new Graphic({
                    geometry: item.geometry,
                    symbol: this.getPolygonColorByLevelId(item.attributes.LEVEL_ID),
                })
                this._graphicsLayer.add(graphic)
            })
        })
    }

    getPolygonColorByLevelId = (levelId: number) => {
        switch (levelId) {
            case 1:
                return this._poloygonMarkerSymbolGreen
            case 2:
                return this._poloygonMarkerSymbolYellow
            case 3:
                return this._poloygonMarkerSymbolRed
            default:
                return this._poloygonMarkerSymbolBlack
        }
    }
}

const mapStoreContext = createContext(new MapStore())
const useMapStore = () => useContext(mapStoreContext)

export default useMapStore
